// Download the family's photos and documents (M54, docs/delete-family.md,
// D192–D193). Everything is opened and zipped on this device: the server
// only hands over what it already stores (ciphertext and signed URLs), and
// the existing endpoints are enough. The zip streams, so where the browser
// can write straight to a file only one file is in memory at a time.
import { downloadZip, makeZip } from 'client-zip';
import {
  api,
  type FamilyDocument,
  type FamilyLink,
  type FamilyListDetail,
} from './api';
import { fetchWithProgress, saveBlob } from './download';
import { documentSecret } from './e2ee/apps';
import type { FileSecret } from './e2ee/crypto';
import { fetchDecryptedBlob } from './e2ee/media';
import type { OpenedE2EE } from './e2ee/session';
import { conversationName } from '../components/chat/messageUtils';
import type { ChatConversation } from '../components/chat/ConversationDialogs';

/** A message as the export reads it, already opened (lib/e2ee). */
export interface ExportMessage {
  id: string;
  type: string;
  content: string;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  deletedAt?: string | null;
  sender?: { displayName: string } | null;
  e2ee?: OpenedE2EE;
}

export interface ExportChat {
  id: string;
  name: string;
}

/** Where the export reads from; the app's API, or a fake in tests. */
export interface ExportSource {
  chats(): Promise<ExportChat[]>;
  /** One page, newest first, as the message endpoint returns them. */
  messages(
    chatId: string,
    cursor?: string
  ): Promise<{ data: ExportMessage[]; hasMore: boolean; cursor?: string }>;
  documents(): Promise<FamilyDocument[]>;
  links(): Promise<FamilyLink[]>;
  lists(): Promise<FamilyListDetail[]>;
  /** Member id → name, for who a list item is for. */
  memberNames(): Promise<Record<string, string>>;
  /** Fetches a file, opening it when it's sealed. */
  file(
    url: string,
    secret: FileSecret | undefined,
    mimeType: string,
    signal?: AbortSignal
  ): Promise<Blob>;
}

export interface PlannedFile {
  path: string;
  url: string;
  secret?: FileSecret;
  mimeType: string;
  lastModified: Date;
  /** Bytes, when the message or document says (for the estimate). */
  size?: number;
}

export interface ExportPlan {
  files: PlannedFile[];
  /** Chat text, Links and Lists: small, held in memory. */
  texts: Array<{ path: string; text: string }>;
  /** What couldn't be included even before downloading (locked records). */
  notIncluded: string[];
  /** About how big the zip will be: known file sizes plus the texts. */
  totalBytes: number;
}

export type ExportProgress =
  | { phase: 'scanning'; done: number; total: number }
  | { phase: 'files'; done: number; total: number };

const MEDIA_LABEL: Record<string, string> = {
  image: 'photo',
  gif: 'GIF',
  video: 'video',
  voice: 'voice note',
  file: 'file',
};

const DEFAULT_NAME: Record<string, string> = {
  image: 'photo',
  gif: 'gif',
  video: 'video',
  voice: 'voice note',
  file: 'file',
};

const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
  'audio/webm': 'webm',
  'audio/mp4': 'm4a',
  'audio/mpeg': 'mp3',
  'audio/ogg': 'ogg',
  'application/pdf': 'pdf',
};

const pad = (value: number) => String(value).padStart(2, '0');

/** "2026-10-25 14:03" in this device's time. */
export function exportTime(iso: string): string {
  const date = new Date(iso);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** A name every zip tool and file system accepts (D192). */
export function safeName(name: string, fallback = 'untitled'): string {
  // eslint-disable-next-line no-control-regex
  let clean = name.replace(/[/\\:*?"<>|\u0000-\u001f]/g, '_').trim();
  clean = clean.replace(/^\.+/, '').trim();
  if (!clean) clean = fallback;
  if (clean.length > 80) {
    const dot = clean.lastIndexOf('.');
    const extension =
      dot > 0 && clean.length - dot <= 10 ? clean.slice(dot) : '';
    clean = clean.slice(0, 80 - extension.length).trimEnd() + extension;
  }
  return clean;
}

/** Keeps paths unique: "photo.jpg", "photo (2).jpg". */
function uniquePaths() {
  const used = new Set<string>();
  return (path: string) => {
    let candidate = path;
    const slash = path.lastIndexOf('/');
    const dot = path.lastIndexOf('.');
    const hasExtension = dot > slash + 1;
    const stem = hasExtension ? path.slice(0, dot) : path;
    const extension = hasExtension ? path.slice(dot) : '';
    for (let n = 2; used.has(candidate.toLowerCase()); n += 1) {
      candidate = `${stem} (${n})${extension}`;
    }
    used.add(candidate.toLowerCase());
    return candidate;
  };
}

function attachmentOf(message: ExportMessage) {
  const metadata = (message.metadata ?? {}) as {
    fileName?: string;
    mimeType?: string;
    files?: string[];
    fileSize?: number;
  };
  const url = metadata.files?.[0] ?? message.content;
  if (!url || !url.startsWith('/uploads/')) return null;
  const meta = message.e2ee?.envelope?.meta;
  const secret = meta && !meta.plainFile ? meta.file : undefined;
  const mimeType = metadata.mimeType ?? meta?.mimeType ?? '';
  let fileName = metadata.fileName ?? meta?.fileName ?? '';
  if (!fileName) {
    const extension = EXTENSIONS[mimeType.split(';')[0] ?? ''];
    fileName = `${DEFAULT_NAME[message.type] ?? 'file'}${extension ? `.${extension}` : ''}`;
  }
  const size =
    typeof metadata.fileSize === 'number'
      ? metadata.fileSize
      : typeof meta?.fileSize === 'number'
        ? meta.fileSize
        : undefined;
  return { url, secret, mimeType, fileName, size };
}

/**
 * Walks every chat you're in, page by page, and the family apps: what goes
 * in the zip and where (D192). Nothing is downloaded yet, so the count of
 * files is known before the slow part starts.
 */
export async function planExport(
  source: ExportSource,
  options: {
    signal?: AbortSignal;
    onProgress?: (progress: ExportProgress) => void;
  } = {}
): Promise<ExportPlan> {
  const { signal, onProgress } = options;
  const unique = uniquePaths();
  const plan: ExportPlan = {
    files: [],
    texts: [],
    notIncluded: [],
    totalBytes: 0,
  };

  const chats = await source.chats();
  const chatFolders = uniquePaths();
  onProgress?.({ phase: 'scanning', done: 0, total: chats.length });
  for (const [index, chat] of chats.entries()) {
    signal?.throwIfAborted();
    const folder = chatFolders(`chats/${safeName(chat.name, 'Chat')}`);
    const pages: ExportMessage[][] = [];
    let cursor: string | undefined;
    for (;;) {
      signal?.throwIfAborted();
      const page = await source.messages(chat.id, cursor);
      pages.push(page.data);
      if (!page.hasMore || !page.cursor) break;
      cursor = page.cursor;
    }
    // Pages come newest first; the text reads oldest first.
    const messages = pages.flat().reverse();
    const lines: string[] = [];
    for (const message of messages) {
      if (message.deletedAt) continue;
      const when = exportTime(message.createdAt);
      if (message.type === 'system') {
        lines.push(`${when} ${message.content}`);
        continue;
      }
      const who = message.sender?.displayName ?? 'Someone';
      if (message.e2ee?.locked) {
        lines.push(`${when} ${who}: [couldn't be opened on this device]`);
        // Its text, and any file it carries, can't be in the zip.
        plan.notIncluded.push(
          `${folder}.txt: a message from ${who} at ${when} (couldn't be opened on this device)`
        );
        continue;
      }
      if (message.type === 'text') {
        lines.push(`${when} ${who}: ${message.content}`);
        continue;
      }
      const attachment = attachmentOf(message);
      const label = MEDIA_LABEL[message.type] ?? 'file';
      if (!attachment) {
        lines.push(`${when} ${who}: [${label}]`);
        continue;
      }
      const path = unique(
        `${folder}/${when.slice(0, 10)} ${safeName(attachment.fileName, 'file')}`
      );
      plan.files.push({
        path,
        url: attachment.url,
        secret: attachment.secret,
        mimeType: attachment.mimeType,
        lastModified: new Date(message.createdAt),
        size: attachment.size,
      });
      const caption = message.e2ee?.envelope?.text?.trim();
      lines.push(
        `${when} ${who}: [${label}: ${path}]${caption ? ` ${caption}` : ''}`
      );
    }
    plan.texts.push({
      path: unique(`${folder}.txt`),
      text: lines.length > 0 ? `${lines.join('\n')}\n` : '',
    });
    onProgress?.({ phase: 'scanning', done: index + 1, total: chats.length });
  }

  signal?.throwIfAborted();
  for (const document of await source.documents()) {
    if (document.e2eeApp?.locked) {
      plan.notIncluded.push(
        `documents: a document added ${exportTime(document.createdAt)} (couldn't be opened on this device)`
      );
      continue;
    }
    const folder = document.folder ? `/${safeName(document.folder)}` : '';
    plan.files.push({
      path: unique(
        `documents${folder}/${safeName(document.fileName, 'document')}`
      ),
      url: document.url,
      secret: documentSecret(document).file,
      mimeType: document.mimeType,
      lastModified: new Date(document.createdAt),
      size: document.fileSize,
    });
  }

  signal?.throwIfAborted();
  const names = await source.memberNames();
  const allLinks = await source.links();
  const lockedLinks = allLinks.filter((link) => link.e2eeApp?.locked).length;
  if (lockedLinks > 0)
    plan.notIncluded.push(
      `links.json: ${lockedLinks} link(s) (couldn't be opened on this device)`
    );
  const links = allLinks
    .filter((link) => !link.e2eeApp?.locked)
    .map((link) => ({
      url: link.url,
      title: link.title,
      note: link.note,
      site: link.siteName,
      addedBy: link.addedByUser?.displayName ?? names[link.addedBy] ?? null,
      addedAt: link.createdAt,
    }));
  plan.texts.push({
    path: unique('links.json'),
    text: `${JSON.stringify(links, null, 2)}\n`,
  });

  signal?.throwIfAborted();
  const allLists = await source.lists();
  const lockedLists = allLists.filter((list) => list.e2eeApp?.locked).length;
  if (lockedLists > 0)
    plan.notIncluded.push(
      `lists.json: ${lockedLists} list(s) (couldn't be opened on this device)`
    );
  const lists = allLists
    .filter((list) => !list.e2eeApp?.locked)
    .map((list) => ({
      name: list.name,
      items: list.items.map((item) => ({
        text: item.text,
        note: item.note,
        done: item.checked,
        for: item.assignedTo ? (names[item.assignedTo] ?? null) : null,
        due: item.dueDate ?? null,
        repeats: item.repeat ?? null,
      })),
    }));
  plan.texts.push({
    path: unique('lists.json'),
    text: `${JSON.stringify(lists, null, 2)}\n`,
  });

  plan.totalBytes =
    plan.files.reduce((sum, file) => sum + (file.size ?? 0), 0) +
    plan.texts.reduce((sum, text) => sum + text.text.length, 0);
  return plan;
}

/** A zip kept in memory (no File System Access API) stays under this. */
export const PART_LIMIT_BYTES = 500 * 1024 * 1024;
/** What a file of unknown size counts as when packing parts. */
export const UNKNOWN_FILE_BYTES = 25 * 1024 * 1024;

/**
 * Splits a plan into zips of at most `maxBytes` each, for browsers that
 * build the zip in memory (phones, Safari): chat text, Links and Lists and
 * what couldn't be opened go in part 1, then files in order, a new part
 * whenever the next file would go over. A file bigger than the limit gets a
 * part to itself. One part when it all fits.
 */
export function splitPlan(
  plan: ExportPlan,
  maxBytes = PART_LIMIT_BYTES
): ExportPlan[] {
  const textBytes = plan.texts.reduce((sum, text) => sum + text.text.length, 0);
  const parts: ExportPlan[] = [
    {
      files: [],
      texts: plan.texts,
      notIncluded: plan.notIncluded,
      totalBytes: textBytes,
    },
  ];
  for (const file of plan.files) {
    // A file that doesn't say its size is packed as if it were 25 MB.
    const size = file.size ?? UNKNOWN_FILE_BYTES;
    let part = parts[parts.length - 1]!;
    const hasContent = part.files.length > 0 || part.texts.length > 0;
    if (hasContent && part.totalBytes + size > maxBytes) {
      part = { files: [], texts: [], notIncluded: [], totalBytes: 0 };
      parts.push(part);
    }
    part.files.push(file);
    part.totalBytes += size;
  }
  return parts;
}

/** "About 1.2 GB" (D192): enough to decide, not exact. */
export function formatSize(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2)
    return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export interface ZipEntry {
  name: string;
  input: Blob | string;
  lastModified: Date;
}

/**
 * The zip's files, one at a time, as the zip writer asks for them: texts
 * first, then each attachment fetched and opened, then not-included.txt
 * for whatever failed. A failed file never stops the rest; cancelling does.
 */
export async function* exportEntries(
  plan: ExportPlan,
  source: Pick<ExportSource, 'file'>,
  options: {
    signal?: AbortSignal;
    onProgress?: (progress: ExportProgress) => void;
    now?: Date;
  } = {}
): AsyncGenerator<ZipEntry> {
  const { signal, onProgress } = options;
  const now = options.now ?? new Date();
  for (const text of plan.texts) {
    signal?.throwIfAborted();
    yield { name: text.path, input: text.text, lastModified: now };
  }
  const notIncluded = [...plan.notIncluded];
  const total = plan.files.length;
  onProgress?.({ phase: 'files', done: 0, total });
  for (const [index, file] of plan.files.entries()) {
    signal?.throwIfAborted();
    let blob: Blob | null = null;
    try {
      blob = await source.file(file.url, file.secret, file.mimeType, signal);
    } catch (err) {
      if (signal?.aborted) throw err;
      notIncluded.push(`${file.path} (couldn't be downloaded or opened)`);
    }
    if (blob) {
      yield { name: file.path, input: blob, lastModified: file.lastModified };
    }
    onProgress?.({ phase: 'files', done: index + 1, total });
  }
  if (notIncluded.length > 0) {
    yield {
      name: 'not-included.txt',
      input: `These couldn't be included on this device:\n${notIncluded.join('\n')}\n`,
      lastModified: now,
    };
  }
}

/** The app's own endpoints (D193): no export-only server route. */
export function apiExportSource(
  familyId: string,
  currentUserId: string | undefined
): ExportSource {
  return {
    async chats() {
      const response = await api.getConversations(familyId);
      return response.data.map((chat) => ({
        id: chat.id,
        name: conversationName(
          chat as unknown as ChatConversation,
          currentUserId
        ),
      }));
    },
    async messages(chatId, cursor) {
      const response = await api.getMessages(chatId, cursor, 100);
      return {
        data: response.data as unknown as ExportMessage[],
        hasMore: response.pagination.hasMore,
        cursor: response.pagination.cursor,
      };
    },
    async documents() {
      const all: FamilyDocument[] = [];
      let cursor: string | undefined;
      for (;;) {
        const page = await api.getFamilyDocuments(familyId, { cursor });
        all.push(...page.data);
        if (!page.pagination.hasMore || !page.pagination.cursor) break;
        cursor = page.pagination.cursor;
      }
      return all;
    },
    async links() {
      const all: FamilyLink[] = [];
      let cursor: string | undefined;
      for (;;) {
        const page = await api.getFamilyLinks(familyId, { cursor });
        all.push(...page.data);
        if (!page.pagination.hasMore || !page.pagination.cursor) break;
        cursor = page.pagination.cursor;
      }
      return all;
    },
    async lists() {
      const lists = (await api.getFamilyLists(familyId)).data;
      const details: FamilyListDetail[] = [];
      for (const list of lists) {
        details.push((await api.getFamilyList(familyId, list.id)).data);
      }
      return details;
    },
    async memberNames() {
      const family = (await api.getFamily(familyId)).data;
      return Object.fromEntries(
        family.members.map((member) => [member.userId, member.user.displayName])
      );
    },
    file(url, secret, mimeType, signal) {
      return secret
        ? fetchDecryptedBlob(url, secret, mimeType, { signal })
        : fetchWithProgress(url, { signal });
    },
  };
}

type SaveFilePicker = (options: {
  suggestedName: string;
  types?: Array<{ description: string; accept: Record<string, string[]> }>;
}) => Promise<{
  createWritable(): Promise<WritableStream<Uint8Array>>;
}>;

/** Where the zip goes: a file chosen now, while the tap still counts. */
export type SaveTarget =
  | { kind: 'file'; writable: WritableStream<Uint8Array> }
  | { kind: 'blob'; fileName: string };

/**
 * Asks where to save where the browser can (desktop Chrome and Edge), so
 * the zip streams to disk; elsewhere (Safari, Firefox, phones) it's put
 * together in memory and handed over as a download (D192). Call it first
 * in the tap's handler. Null: the person closed the picker.
 */
export async function chooseSaveTarget(
  fileName: string
): Promise<SaveTarget | null> {
  const picker = (window as unknown as { showSaveFilePicker?: SaveFilePicker })
    .showSaveFilePicker;
  if (!picker) return { kind: 'blob', fileName };
  try {
    const handle = await picker({
      suggestedName: fileName,
      types: [{ description: 'Zip', accept: { 'application/zip': ['.zip'] } }],
    });
    return { kind: 'file', writable: await handle.createWritable() };
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') return null;
    // Some browsers refuse the picker here: fall back to a download.
    return { kind: 'blob', fileName };
  }
}

/** Writes the zip to the chosen place; rejects if cancelled. */
export async function writeZip(
  entries: AsyncIterable<ZipEntry>,
  target: SaveTarget,
  signal?: AbortSignal
) {
  if (target.kind === 'file') {
    await makeZip(entries).pipeTo(target.writable, { signal });
    return;
  }
  const blob = await downloadZip(entries).blob();
  signal?.throwIfAborted();
  saveBlob(blob, target.fileName);
}

/** "The Sharma Family 2026-10-25.zip", or "… part2.zip" when split. */
export function exportFileName(
  familyName: string,
  now = new Date(),
  part?: number
) {
  const suffix = part ? ` part${part}` : '';
  return `${safeName(familyName, 'Twinden')} ${exportTime(now.toISOString()).slice(0, 10)}${suffix}.zip`;
}

/** Whether the zip can stream to a file here (desktop Chrome and Edge). */
export function canStreamToFile(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as unknown as { showSaveFilePicker?: unknown })
      .showSaveFilePicker === 'function'
  );
}
